import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { useCheckoutStateStore } from '../../checkout/stores/checkoutStateStore';
import { useClickAndCollectLocationStore } from './clickAndCollectLocationStore';
import { OrderType } from './orderTypeStore.model';

export const useOrderTypeStore = defineStore('orderType', () => {
  // State
  const orderType = ref<OrderType>('online');

  // Getters
  const isClickAndCollect = computed(() => orderType.value === 'clickAndCollect');
  // Whether click & collect can be selected at all: the feature toggle is on
  // AND a store has actually resolved. Single source of truth composed from
  // `checkoutStateStore` (feature toggle) and `clickAndCollectLocationStore`
  // (store availability) so components don't re-derive this themselves.
  const canUseClickAndCollect = computed(() => {
    const checkoutStateStore = useCheckoutStateStore();
    const clickAndCollectLocationStore = useClickAndCollectLocationStore();
    return checkoutStateStore.clickAndCollectEnabled && clickAndCollectLocationStore.hasStore;
  });
  const isClickAndCollectActive = computed(
    () => isClickAndCollect.value && canUseClickAndCollect.value
  );

  // Actions
  function setOrderType(type: OrderType) {
    orderType.value = type;
  }

  // Eagerly resolves click & collect store availability (if the feature
  // toggle is on) so `canUseClickAndCollect` is known before the toggle
  // would otherwise render, instead of only discovering "no store" after the
  // customer has already selected click & collect.
  async function initClickAndCollectAvailability(): Promise<void> {
    const checkoutStateStore = useCheckoutStateStore();
    if (!checkoutStateStore.clickAndCollectEnabled) return;

    const clickAndCollectLocationStore = useClickAndCollectLocationStore();
    if (!clickAndCollectLocationStore.isLoaded) {
      await clickAndCollectLocationStore.fetchStore();
    }
  }

  function reset() {
    orderType.value = 'online';
  }

  return {
    // State
    orderType,
    // Getters
    isClickAndCollect,
    canUseClickAndCollect,
    isClickAndCollectActive,
    // Actions
    setOrderType,
    initClickAndCollectAvailability,
    reset,
  };
});
